import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";

const ReusableDataGrid = ({
  rows = [],
  columns = [],
  loading = false,
  pageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  noRowsMessage = "No data available to display",
  getRowSx,
  getCellSx,
  onRowClick,
  onVisibleRowsChange,
  disableSorting = false,
  disablePagination = false,
  disableInternalScroll = false,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortField(field);
      setSortDir("asc");
    }
    setPage(0);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(0);
  };

  const getCellValue = (col, row) => {
    if (col.valueGetter) return col.valueGetter(row[col.field], row);
    return row[col.field] ?? "";
  };

  const sortedRows = useMemo(() => {
    if (!sortField || disableSorting) return rows;
    const col = columns.find((c) => c.field === sortField);
    return [...rows].sort((a, b) => {
      const aVal = col ? getCellValue(col, a) : (a[sortField] ?? "");
      const bVal = col ? getCellValue(col, b) : (b[sortField] ?? "");
      const cmp = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [rows, sortField, sortDir, columns, disableSorting]);

  const pageCount = Math.ceil(sortedRows.length / rowsPerPage);
  const startRecord = sortedRows.length === 0 ? 0 : page * rowsPerPage + 1;
  const endRecord = Math.min(
    page * rowsPerPage + rowsPerPage,
    sortedRows.length,
  );
  const totalRecords = sortedRows.length;

  const paginatedRows = disablePagination
    ? sortedRows
    : sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => {
    onVisibleRowsChange?.(paginatedRows);
  }, [onVisibleRowsChange, paginatedRows]);

  const visibleColumns = columns.filter((c) => c.field !== "actions");
  const actionsColumn = columns.find((c) => c.field === "actions");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: disableInternalScroll ? "auto" : "100%",
        width: "100%",
      }}
    >
      {/* ── Scrollable Table ── */}
      <TableContainer
        sx={{
          flex: disableInternalScroll ? "unset" : 1,
          overflowY: disableInternalScroll ? "visible" : "auto",
          overflowX: disableInternalScroll ? "visible" : "auto",
          width: "100%",
          scrollbarGutter: disableInternalScroll ? "auto" : "stable both-edges",
          pr: disableInternalScroll ? 0 : 1,
          pb: disableInternalScroll ? 0 : 1,
          boxSizing: "border-box",
          "&::-webkit-scrollbar": { width: 8, height: 8 },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: 4,
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#c1c1c1",
            borderRadius: 4,
            "&:hover": { background: "#888" },
          },
        }}
      >
        <Table
          stickyHeader={!disableInternalScroll}
          size="medium"
          sx={{ width: "100%", tableLayout: "auto" }}
        >
          <TableHead>
            <TableRow>
              {visibleColumns.map((col) => (
                <TableCell
                  key={col.field}
                  align={col.headerAlign || col.align || "left"}
                  style={{ minWidth: col.minWidth || col.width || 80 }}
                  sx={{
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #e0e0e0",
                    fontWeight: 700,
                    color: "#344054",
                    whiteSpace: "nowrap",
                    py: 1.5,
                  }}
                >
                  {!disableSorting && col.sortable !== false ? (
                    <TableSortLabel
                      active={sortField === col.field}
                      direction={sortField === col.field ? sortDir : "asc"}
                      onClick={() => handleSort(col.field)}
                      sx={{
                        "&.Mui-active": { color: "#667eea" },
                        "& .MuiTableSortLabel-icon": {
                          color: "#667eea !important",
                        },
                      }}
                    >
                      {col.headerName}
                    </TableSortLabel>
                  ) : (
                    col.headerName
                  )}
                </TableCell>
              ))}
              {actionsColumn && (
                <TableCell
                  align="center"
                  style={{ minWidth: actionsColumn.width || 120 }}
                  sx={{
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #e0e0e0",
                    fontWeight: 700,
                    color: "#344054",
                    whiteSpace: "nowrap",
                    py: 1.5,
                  }}
                >
                  {actionsColumn.headerName}
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ py: 8, border: "none" }}
                >
                  <CircularProgress sx={{ color: "#667eea" }} />
                </TableCell>
              </TableRow>
            ) : paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ py: 8, border: "none", color: "text.secondary" }}
                >
                  {noRowsMessage}
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row, rowIndex) => (
                <TableRow
                  key={row.id ?? rowIndex}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{
                    "&:hover": { backgroundColor: "rgba(102,126,234,0.04)" },
                    "&:last-child td": { border: 0 },
                    cursor: onRowClick ? "pointer" : "default",
                    // ── Per-row style injection ──
                    ...(getRowSx ? getRowSx(row) : {}),
                  }}
                >
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={col.field}
                      align={col.align || "left"}
                      style={{ minWidth: col.minWidth || col.width || 80 }}
                      sx={{
                        borderBottom: "1px solid #f0f0f0",
                        py: 1.2,
                        color: "#344054",
                        // ── Per-cell style injection ──
                        ...(getCellSx ? getCellSx(row, col) : {}),
                      }}
                    >
                      {col.renderCell
                        ? col.renderCell({ row, value: getCellValue(col, row) })
                        : getCellValue(col, row)}
                    </TableCell>
                  ))}
                  {actionsColumn && (
                    <TableCell
                      align="center"
                      onClick={(event) => event.stopPropagation()}
                      sx={{ borderBottom: "1px solid #f0f0f0", py: 1.2 }}
                    >
                      {actionsColumn.renderCell?.({ row, value: null })}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Footer: hidden when disablePagination ── */}
      {!disablePagination && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1,
            borderTop: "2px solid #e0e0e0",
            backgroundColor: "#fff",
            flexShrink: 0,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography
            sx={{ fontSize: "0.8rem", color: "#667788", whiteSpace: "nowrap" }}
          >
            {loading
              ? "Loading..."
              : totalRecords === 0
                ? "No records"
                : `Showing ${startRecord}–${endRecord} of ${totalRecords} record${totalRecords !== 1 ? "s" : ""}`}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "#667788",
                  whiteSpace: "nowrap",
                }}
              >
                Rows per page:
              </Typography>
              <FormControl size="small" variant="outlined">
                <Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  sx={{
                    fontSize: "0.8rem",
                    height: 30,
                    minWidth: 64,
                    color: "#344054",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#667eea",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#667eea",
                    },
                  }}
                >
                  {pageSizeOptions.map((opt) => (
                    <MenuItem key={opt} value={opt} sx={{ fontSize: "0.8rem" }}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Pagination
              count={pageCount}
              page={page + 1}
              onChange={(_, value) => setPage(value - 1)}
              color="primary"
              showFirstButton
              showLastButton
              size="small"
              sx={{
                "& .MuiPaginationItem-root": { borderRadius: "4px" },
                "& .MuiPaginationItem-page.Mui-selected": {
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "#fff",
                  border: "none",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5568d3 0%, #65408b 100%)",
                  },
                },
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ReusableDataGrid;
